import { X } from 'lucide-react';
import { useState } from 'react';

interface RejectReasonSelectorProps {
  reasons: string[];
  setNote: (note: string) => void;
}

const RejectReasonSelector: React.FC<RejectReasonSelectorProps> = (props: RejectReasonSelectorProps) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [selectedReasons, setSelectedReasons] = useState<string[]>([]);
  const [extraReason, setExtraReason] = useState<string>('');

  const toggleReason = (reason: string) => {
    let newSelectedReasons: string[] = [];

    if (selectedReasons.includes(reason)) {
      newSelectedReasons = selectedReasons.filter(r => r !== reason);
    } else {
      newSelectedReasons = [...selectedReasons, reason];
    }
    const allReasons = extraReason.trim() ? [...newSelectedReasons, extraReason.trim()] : newSelectedReasons;
    props.setNote(allReasons.join(', '));
    setSelectedReasons(newSelectedReasons);
  };

  const removeReason = (reason: string) => {
    const newSelectedReasons = selectedReasons.filter(r => r !== reason);
    const allReasons = [...newSelectedReasons, extraReason.trim()];
    props.setNote(allReasons.join(', '));
    setSelectedReasons(newSelectedReasons);
  };

  const handleExtraReasonChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    const extraReason = event.target.value;
    const allReasons = [...selectedReasons, extraReason.trim()];
    props.setNote(allReasons.join(', '));
    setExtraReason(extraReason);
  };

  return (
    <div className="w-full mb-4">
      <label className="text-sm font-medium text-gray-700 mb-2 flex items-center justify-between">
        <span>เลือกเหตุผลในการไม่อนุมัติ:</span>
        <button
          onClick={() => setIsDropdownOpen(!isDropdownOpen)}
          className="text-sm text-blue-500 hover:text-blue-700"
        >
          {isDropdownOpen ? 'ปิดรายการ' : 'เปิดรายการ'}
        </button>
      </label>

      {/* Selected Reasons Display */}
      <div className="flex flex-wrap gap-2 mb-2">
        {selectedReasons.length === 0 && (
          <span className="text-gray-400 py-1 text-sm italic">ยังไม่มีเหตุผลที่เลือก</span>
        )}

        {selectedReasons.map((reason, index) => (
          <span
            key={index}
            className="bg-red-50 text-red-700 text-sm rounded-full px-3 py-1 flex items-center"
          >
            {reason}
            <button
              onClick={() => removeReason(reason)}
              className="ml-1.5 text-red-500 hover:text-red-700"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </span>
        ))}
      </div>

      {/* Compact Reason Selection Dropdown */}
      {isDropdownOpen && (
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm mb-2 max-h-40 overflow-y-auto">
          <div className="grid grid-cols-1 divide-y divide-gray-100">
            {props.reasons.map((reason, index) => (
              <div
                key={index}
                className="flex items-center px-3 py-2 hover:bg-gray-50 cursor-pointer"
                onClick={() => toggleReason(reason)}
              >
                <input
                  type="checkbox"
                  id={`reason-${index}`}
                  checked={selectedReasons.includes(reason)}
                  onChange={() => { }} // Handle with parent onClick
                  className="h-4 w-4 text-red-600 rounded"
                />
                <label htmlFor={`reason-${index}`} className="ml-2 block text-sm text-gray-700 cursor-pointer">
                  {reason}
                </label>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Extra reason textarea */}
      <div className="mt-3">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          เหตุผลเพิ่มเติม (ถ้ามี):
        </label>
        <textarea
          value={extraReason}
          onChange={handleExtraReasonChange}
          className="w-full p-2.5 rounded-md border border-gray-300"
          rows={2}
          placeholder="ระบุเหตุผลอื่นๆ..."
        />
      </div>
    </div>
  );
};

export default RejectReasonSelector;